/**
 * CompletionCelebration — DRR-309 Phase 4
 *
 * Celebration screen when a journey completes successfully.
 * Shows confetti animation and completion summary.
 */

import React, { useEffect, useState } from 'react';
import {
  Stack,
  Group,
  Text,
  ThemeIcon,
  Paper,
  Button,
  Badge,
  Center,
} from '@mantine/core';
import { IconCheck, IconConfetti, IconArrowRight } from '@tabler/icons-react';
import confetti from 'canvas-confetti';

export interface CompletionCelebrationProps {
  subjectName: string;
  goalName: string;
  completedAt: Date;
  totalTasks: number;
  daysTaken: number;
  onContinue?: () => void;
  onViewSummary?: () => void;
}

export function CompletionCelebration({
  subjectName,
  goalName,
  completedAt,
  totalTasks,
  daysTaken,
  onContinue,
  onViewSummary,
}: CompletionCelebrationProps) {
  const [showConfetti, setShowConfetti] = useState(true);

  // Fire confetti animation on mount
  useEffect(() => {
    // Fire multiple bursts for celebratory effect
    const fireConfetti = () => {
      // Center burst
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#40c057', '#51cf66', '#69db7c', '#8ce99a', '#ffd43b'],
      });

      // Left side burst
      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.6 },
          colors: ['#40c057', '#51cf66', '#69db7c'],
        });
      }, 250);

      // Right side burst
      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.6 },
          colors: ['#40c057', '#51cf66', '#69db7c'],
        });
      }, 400);
    };

    fireConfetti();

    // Hide confetti icon after animation
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Paper p="xl" withBorder bg="green.0">
      <Stack gap="lg" align="center">
        {/* Confetti indicator (visual only, actual confetti would use a library) */}
        {showConfetti && (
          <Center>
            <ThemeIcon size={80} radius="xl" color="green" variant="filled">
              <IconConfetti size={48} />
            </ThemeIcon>
          </Center>
        )}

        {/* Success icon */}
        <ThemeIcon size={60} radius="xl" color="green" variant="light">
          <IconCheck size={36} />
        </ThemeIcon>

        {/* Completion message */}
        <Stack gap="xs" align="center">
          <Text size="xl" fw={700} ta="center">
            Journey Complete!
          </Text>
          <Text size="lg" c="dimmed" ta="center">
            {subjectName}'s {goalName} has been successfully completed.
          </Text>
        </Stack>

        {/* Stats */}
        <Group gap="xl">
          <Stack align="center" gap={4}>
            <Text size="xl" fw={700} c="green">
              {totalTasks}
            </Text>
            <Text size="xs" c="dimmed">
              Tasks Completed
            </Text>
          </Stack>

          <Stack align="center" gap={4}>
            <Text size="xl" fw={700} c="green">
              {daysTaken}
            </Text>
            <Text size="xs" c="dimmed">
              Days
            </Text>
          </Stack>

          <Stack align="center" gap={4}>
            <Badge size="lg" color="green" variant="light">
              {completedAt.toLocaleDateString()}
            </Badge>
            <Text size="xs" c="dimmed">
              Completed
            </Text>
          </Stack>
        </Group>

        {/* Actions */}
        <Group>
          {onViewSummary && (
            <Button variant="light" color="green" onClick={onViewSummary}>
              View Summary
            </Button>
          )}
          {onContinue && (
            <Button
              color="green"
              rightSection={<IconArrowRight size={16} />}
              onClick={onContinue}
            >
              Continue
            </Button>
          )}
        </Group>
      </Stack>
    </Paper>
  );
}

export default CompletionCelebration;
